import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';
import PullToRefresh from 'pulltorefreshjs';

const standalone = window.matchMedia('(display-mode: standalone)').matches;

// ✅ Reload safety flag
let hasReloaded = false;
const safeReload = () => {
  if (hasReloaded) return;
  hasReloaded = true;
  console.log('♻️ Reloading app...');
  window.location.reload();
};

const isIOS = () => {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

// ✅ Pull to refresh for iOS standalone
if (standalone && isIOS()) {
  PullToRefresh.init({
    onRefresh() {
      safeReload();
    },
  });
}

// ✅ Version checker with forced clear for problematic versions
const checkForNewVersion = async () => {
  try {
    const response = await fetch('/version.json?v=' + Date.now(), {
      cache: 'no-store',
    });
    const data = await response.json();
    const currentVersion = data.version;
    const storedVersion = localStorage.getItem('app_version');

    // 🚨 Force clear for users with bugged versions
    const buggedVersions = ['1.0.93', '1.0.92']; // Add problematic versions here
    const shouldForceClear =
      !storedVersion || buggedVersions.includes(storedVersion);

    if (
      shouldForceClear ||
      (storedVersion && storedVersion !== currentVersion)
    ) {
      if (shouldForceClear) {
        console.log('🚨 Clearing bugged version data...');
      } else {
        console.log('🆕 New version detected. Refreshing...');
      }

      // Clear everything (SW cache, LS, cookies) - but preserve auth for normal updates
      if (shouldForceClear) {
        await clearServiceWorkerCaches(true); // Force total clear
      } else {
        await clearServiceWorkerCaches(false); // Preserve auth
      }

      localStorage.setItem('app_version', currentVersion);
      window.location.reload(); // force reload
    } else {
      localStorage.setItem('app_version', currentVersion);
    }
  } catch (error) {
    console.error('❌ Version check failed:', error);
  }
};

const clearServiceWorkerCaches = async (forceTotal = false) => {
  // Unregister all SWs
  const regs = await navigator.serviceWorker.getRegistrations();
  for (const reg of regs) await reg.unregister();

  // Delete all caches
  const keys = await caches.keys();
  await Promise.all(keys.map((key) => caches.delete(key)));

  // Clear cookies
  document.cookie.split(';').forEach((c) => {
    document.cookie = c
      .replace(/^ +/, '')
      .replace(/=.*/, '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/');
  });

  if (forceTotal) {
    // 🚨 Total clear for bugged versions - remove everything
    console.log('🧹 Force clearing all localStorage...');
    localStorage.clear();
    sessionStorage.clear();
  } else {
    // ✅ Preserve important user data for normal updates
    const token = localStorage.getItem('token');
    const userPreferences = localStorage.getItem('userPreferences');

    localStorage.clear();

    // ✅ Restore preserved data
    if (token) localStorage.setItem('token', token);
    if (userPreferences)
      localStorage.setItem('userPreferences', userPreferences);
  }
};

// ✅ Emergency reset if URL contains ?reset=true
if (window.location.search.includes('reset=true')) {
  console.log('🚨 Emergency reset triggered!');
  localStorage.clear();
  sessionStorage.clear();

  // Clear all caches
  if ('caches' in window) {
    caches.keys().then((keys) => {
      keys.forEach((key) => caches.delete(key));
    });
  }

  // Remove reset parameter and reload
  const url = new URL(window.location);
  url.searchParams.delete('reset');
  window.history.replaceState({}, '', url);
  window.location.reload();
}

// ✅ Check version before app loads
window.onload = () => {
  checkForNewVersion(); // 🆕 always check on launch
};

// ✅ Register service worker with SKIP_WAITING
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log(
          '✅ ServiceWorker registered with scope:',
          registration.scope,
        );

        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          if (installingWorker) {
            installingWorker.onstatechange = () => {
              if (
                installingWorker.state === 'installed' &&
                navigator.serviceWorker.controller
              ) {
                console.log('🚀 New service worker installed. Activating...');
                installingWorker.postMessage({ type: 'SKIP_WAITING' });
              }
            };
          }
        };
      })
      .catch((error) => {
        console.log('❌ ServiceWorker registration failed:', error);
      });

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('🔁 SW controller changed. Reloading...');
      safeReload();
    });
  });
}

// ✅ Mount app
const root = createRoot(document.getElementById('root'));
root.render(<App />);

// 📈 Optional performance monitoring
reportWebVitals();
